import {Component, EventEmitter, Inject, Input, OnInit, Output} from '@angular/core';
import * as _ from 'lodash';

import { DialogService } from '@app/common/services/dialog.service';
import { ConfirmationComponent } from '@app/common/components/confirmation/confirmation.component';
import { ClassName } from '@app/common/enums/class-name.enum';
import { ListModel } from '@app/common/models/list.model';
import { ListService } from '@app/common/services/list.service';
import { AuthService } from '@app/common/services/auth.service';
import { AttachmentModel } from '../../models/attachmentModel';
import { AttachmentEditFormComponent } from '@app/common/components/attachment-edit-form/attachment-edit-form.component';
import { UploadFileExtended } from '@app/common/components/fileupload/fileupload.component';
import { DisabledAttachmentType, AttachmentChooseTypeFormComponent } from '@app/common/components/attachment-choose-type-form/attachment-choose-type-form.component';
import { FileUtils } from '@app/common/utils/file.utils';
import { AttachmentTypeModel } from '@app/common/models/attachment-type.model';
import { Restangular } from 'ngx-restangular';
import { UploadService } from '@app/common/services/upload.service';
import { APP_BRAND } from '@app/common/services/config.service';
import {ComponentModel} from "@app/common/models/component.model";

@Component({
  selector: 'gmt-attachments',
  templateUrl: './attachments.component.html',
  styleUrls: ['./attachments.component.scss']
})
export class AttachmentsComponent implements OnInit {

  public static readonly VIEWER_URL = window.location.pathname + `pdf-viewer/web/viewer.html?file=`;

  // bindings
  @Input() defaultData: any = {};
  @Input() defaultAttachmentType: AttachmentTypeModel;
  @Input() attachmentList: ListModel<AttachmentModel>;
  @Input() allowType: boolean;
  @Input() maxHeight: number;
  @Input() authorizedUser: boolean;
  @Input() filesSelectSetter: any;
  @Input() draggable = true;
  @Input() attachmentCategory: 'case' | 'gp' | 'o' | 'subject' | 'sample' | 'doss' = 'case';
  @Input() disabledAttachmentTypes: DisabledAttachmentType[];
  @Input() mapEnabled = false;
  @Output() updated = new EventEmitter<boolean>();

  // model
  restBaseUrl: string;
  authToken: string;
  uploadingFiles: UploadFileExtended[] = [];

  constructor(
    private restangular: Restangular,
    private uploadService: UploadService,
    private listService: ListService,
    private authService: AuthService,
    private dialogService: DialogService,
    @Inject(APP_BRAND) public APP_BRAND: any,
  ) {
    this.onSort = this.onSort.bind(this);
    this.onFileSelect = this.onFileSelect.bind(this);
    this.onFileSelectExternal = this.onFileSelectExternal.bind(this);
    this.onEditAttachment = this.onEditAttachment.bind(this);
    this.onDeleteAttachment = this.onDeleteAttachment.bind(this);
    this.onCancelledCheckboxClicked = this.onCancelledCheckboxClicked.bind(this);
    this.checkCancelled = this.checkCancelled.bind(this);
  }

  ngOnInit() {
    if (this.filesSelectSetter) {
      this.filesSelectSetter(this.onFileSelectExternal);
    }
    this.restBaseUrl = this.authService.getActiveApplicationRestUrl();
    if (this.restBaseUrl.indexOf('http://') !== 0 && this.restBaseUrl.indexOf('https://') !== 0) {
      if (this.restBaseUrl.indexOf('/') !== 0 ) {
        this.restBaseUrl = window.location.pathname + this.restBaseUrl;
      }
      this.restBaseUrl = window.location.protocol + '//' + window.location.host + this.restBaseUrl;
    }
    this.authToken = this.authService.getToken();
  }

  async onFileSelect(files: UploadFileExtended[]) {
    if (files.length === 1) {
      const dialog = this.dialogService.open(AttachmentChooseTypeFormComponent, {
        data: {
          allowType: this.allowType,
          attachmentCategory: this.attachmentCategory,
          file: files[0],
          defaultAttachmentType: this.defaultAttachmentType,
          disabledAttachmentTypes: this.disabledAttachmentTypes
        },
        className: ClassName.HIGHER_DIALOG,
      });

      const sub = dialog.afterClosed.subscribe((result: { type: string, note: string, baseName: string } | boolean) => {
        if (typeof result === 'object') {
          files
            .filter(file => !file.uploading)
            .forEach(file => this.startUpload(file, result.type, result.note, result.baseName));
        } else {
          this.uploadingFiles.splice(0, this.uploadingFiles.length);
          files.splice(0, files.length);
        }
        sub.unsubscribe();
      });
    } else {
      for (const file of files.filter(file => !file.uploading)) {
        await this.startUpload(file);
      }
    }
  }

  onFileSelectExternal(files: File[]) {
    const extendedFiles = files.map(file => {
      return FileUtils.fileToUpload(file, () => true);
    });
    this.onFileSelect(extendedFiles);
  }

  onFilterChanged() {
    return this.listService.fetchResult(this.attachmentList);
  }

  checkCancelled() {
    return this.attachmentList.filter.filters.cancelled !== false;
  }

  onCancelledCheckboxClicked() {
    if (this.checkCancelled()) {
      this.attachmentList.filter.filters.cancelled = false;
    } else {
      delete this.attachmentList.filter.filters.cancelled;
    }
    this.onFilterChanged();
  }

  onSort(sortValue: any, sortDir: any) {
    this.listService.sort(this.attachmentList, sortValue, sortDir);
  }

  onEditAttachment(attachment: AttachmentModel) {
    const dialog = this.dialogService.open(AttachmentEditFormComponent, {
      data: {
        allowType: this.allowType,
        attachment: {...attachment},
        attachmentCategory: this.attachmentCategory,
        disabledAttachmentTypes: this.disabledAttachmentTypes
      },
      className: ClassName.HIGHER_DIALOG,
    });

    const sub = dialog.afterClosed.subscribe((result: AttachmentModel | boolean) => {
      if (result) {
          const index = _.findIndex(this.attachmentList.list, attachment);
          this.attachmentList.list.splice(index, 1, result as AttachmentModel);
          this.updated.emit(true);
      }
      sub.unsubscribe();
    });
  }

  onDeleteAttachment(attachment: AttachmentModel) {
    const dialog = this.dialogService.open(ConfirmationComponent, {
      data: {
        msg: 'Opravdu chcete soubor smazat? Jeho obnovení nebude možné!',
      },
      className: ClassName.ADJUSTED_DIALOG,
    });

    const sub = dialog.afterClosed.subscribe((result: boolean) => {
      if (result) {
        this.restangular.one('attachments', attachment.id).remove().toPromise().then(() => {
          if (!this.checkCancelled()) {
            this.attachmentList.list = _.without(this.attachmentList.list, attachment);
          } else {
            attachment.cancelled = true;
          }
          this.attachmentList.itemCount--;
          this.updated.emit(true);
        });
      }
      sub.unsubscribe();
    });
  }

  getAttachmentUrl(attachment: AttachmentModel) {
    return `${AttachmentsComponent.VIEWER_URL}${this.restBaseUrl}/attachments/${attachment.id}/data-pdf%3Ft%3D${this.authToken}`;
  }

  canModify(permissions, user?) {
    return (
      (this.authService.hasPermission(permissions) || this.authService.isAuthorized())
      &&
      (!user || this.authService.isAuthorized(user) || this.authorizedUser)
    );
  }

  fileType(attachment: AttachmentModel, type: 'vfk'): boolean {
    const extension = FileUtils.getFileExtension(
      { name: (attachment.baseName + (attachment.extension ? '.' + attachment.extension : '')) }
    );

    const isVfk = extension === 'vfk';
    return type === 'vfk' && isVfk;
  }

  private startUpload(file: UploadFileExtended, type = '', note = '', baseName?) {
    // if uploaded from external source (not by file-upload)
    if (this.uploadingFiles.indexOf(file) === -1) {
      this.uploadingFiles.push(file);
    }

    file.progress = 0;

    let sendData: any = {
      file: file.fileEntry,
      filename: file.fileEntry.name,
      baseName: baseName,
    };

    if (this.defaultData) {
      sendData = {...sendData, ...this.defaultData};
    }

    if (type) {
      sendData.attachmentType = type;
    }

    if (note) {
      sendData.note = note;
    }

    file.uploading = this.uploadService.upload({
      url: this.restBaseUrl + '/attachments',
      data: sendData,
      headers: { Authorization: this.authToken }
    }).progress(event => {
      file.progress = (100.0 * event.loaded / event.total);
    }).success(data => {
      this.uploadingFiles.splice(this.uploadingFiles.indexOf(file), 1);
      this.attachmentList.list.unshift(data as AttachmentModel);
      this.attachmentList.itemCount++;
      this.updated.emit(true);
    }).error(() => {
      file.error = true;
    });

    return file.uploading;
  }
}
